import { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, IconButton, Popover, Slider, Typography } from '@material-ui/core';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SettingsIcon from '@material-ui/icons/Settings';
import { playNotificationSound } from '../../utils/notificationSound';

// Cores do slider — verde vivo para trilha, verde pastel para rail, branco+borda para thumb
const TRACK_COLOR = '#4ade80';
const RAIL_COLOR = '#dcfce7';
const THUMB_BORDER = '#4ade80';
const THUMB_SHADOW = 'rgba(74,222,128,0.18)';

const useStyles = makeStyles((theme) => ({
  iconButton: (props) => ({
    color: '#fff',
    width: 40,
    height: 40,
    backgroundColor: props.primaryColor || theme.palette.primary.main,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: props.primaryColor || theme.palette.primary.dark,
      filter: 'brightness(0.9)',
      transform: 'scale(1.05)',
    },
  }),

  slider: {
    color: TRACK_COLOR,
    padding: '6px 0',
    '& .MuiSlider-track': {
      height: 5,
      borderRadius: 3,
      backgroundColor: TRACK_COLOR,
      border: 'none',
    },
    '& .MuiSlider-rail': {
      height: 5,
      borderRadius: 2,
      backgroundColor: RAIL_COLOR,
      opacity: 1,
    },
    '& .MuiSlider-thumb': {
      width: 14,
      height: 14,
      marginTop: -4.5,
      marginLeft: -7,
      backgroundColor: '#ffffff',
      border: `2px solid ${THUMB_BORDER}`,
      boxShadow: 'none',
      '&:hover, &.Mui-focusVisible': {
        boxShadow: `0 0 0 6px ${THUMB_SHADOW}`,
      },
    },
  },

  pushButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    borderRadius: 8,
    border: '1px solid #bbf7d0',
    backgroundColor: '#f0fdf4',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#dcfce7',
    },
  },
}));

const NotificationSettingsPopover = ({ volume, setVolume, onOpenNotifications, primaryColor }) => {
  const classes = useStyles({ primaryColor });
  const anchorRef = useRef();
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const handleVolumeChange = (_, value) => {
    const v = Number(value);
    if (!Number.isFinite(v)) return;
    const clamped = Math.min(1, Math.max(0, v));
    setVolume(clamped);
    localStorage.setItem('volume', String(clamped));
    localStorage.setItem('notificationSoundMuted', clamped <= 0.01 ? 'true' : 'false');
    if (clamped > 0.01) playNotificationSound();
  };

  return (
    <>
      <IconButton
        className={classes.iconButton}
        onClick={handleToggle}
        ref={anchorRef}
        title="Notificações"
      >
        <NotificationsActiveIcon />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        PaperProps={{
          style: {
            borderRadius: 12,
            width: 224,
            padding: '12px 12px 10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          },
        }}
      >
        {/* Header: título + engrenagem */}
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <Typography style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
            Configurações
          </Typography>
          <SettingsIcon style={{ fontSize: 15, color: '#9ca3af' }} />
        </Box>

        {/* Label volume */}
        <Typography style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>
          Volume Notificações
        </Typography>

        {/* Slider + percentual */}
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Slider
            className={classes.slider}
            value={Number(volume)}
            step={0.01}
            min={0}
            max={1}
            onChange={handleVolumeChange}
            style={{ flex: 1 }}
          />
          <Typography
            style={{
              fontSize: 11,
              color: TRACK_COLOR,
              fontWeight: 500,
              minWidth: 28,
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {Math.round(volume * 100)}%
          </Typography>
        </Box>

        {/* Divider */}
        <Box style={{ height: 1, backgroundColor: '#f3f4f6', margin: '15px 0px 13px' }} />

        {/* Botão Notificações push */}
        <button
          className={classes.pushButton}
          onClick={() => {
            onOpenNotifications();
            handleClose();
          }}
        >
          <NotificationsActiveOutlinedIcon size={13} style={{ color: '#10b981' }} />
          <Typography style={{ fontSize: 14, color: '#059669', fontWeight: 400, lineHeight: 1.3 }}>
            Notificações push
          </Typography>
        </button>
      </Popover>
    </>
  );
};

export default NotificationSettingsPopover;
